// 9fbef606107a605d69c0edbcd8029e5d
import {
  getMapSliceForFit,
  getMapSliceForColor,
} from '../../../features/browse/ProductListing/molecules/ProductList/utils/productsCommonUtils';
import { BOPIS_ITEM_AVAILABILITY } from '../PickupStoreModal/PickUpStoreModal.constants';

const config = {
  SKU_DETAILS: {
    color: 'color',
    fit: 'fit',
    size: 'size',
    quantity: 'quantity',
    distance: 'distance',
  },
};

/**
 * @method showFitsForProduct
 * @description this method returns the bool value, to show product
 * fits properties
 */
const showFitsForProduct = (productInfo, initialValues) => {
  const currentColorEntry = getMapSliceForColor(productInfo.colorFitsSizesMap, initialValues.color);
  return currentColorEntry && currentColorEntry.hasFits;
};

/**
 * @method validateSkuDetails
 * @description Validate SKU detils if SKU is resolved or not
 */
const validateSkuDetails = (productInfo, initialValues) => {
  // eslint-disable-next-line
  for (let key in initialValues) {
    const value =
      typeof initialValues[key] === 'object'
        ? initialValues[key] && initialValues[key].name
        : initialValues[key];
    if (
      !value &&
      (key.toLowerCase() !== config.SKU_DETAILS.fit ||
        (key.toLowerCase() === config.SKU_DETAILS.fit &&
          showFitsForProduct(productInfo, initialValues)))
    ) {
      return false;
    }
  }
  return true;
};

/**
 * @method validateBossEligibility
 * @description checks the bopis product flags for returning the boolean
 * value
 * @param {bool} isBossClearanceProductEnabled this flag is derived through the kill
 * Switch API. If a product is of Clearance then we need to check this kill switch
 * @param {bool} isBossEnabled this flag is a global bopss Enability flag, which is
 * derived from the UserRegisteredInfo API. Backend sends this flag along with
 * validating country and state bopis availability
 * @param {object} miscInfo object data with pickup eligibility of product
 */

function validateBossEligibility({ isBossClearanceProductEnabled, isBossEnabled, miscInfo }) {
  const bossEligibility = isBossEnabled && miscInfo && miscInfo.isBossEligible;
  // adding this check as productDynamicAbstractor and cartDynamicAbstractor return
  // different keys for clearance item identification
  return miscInfo && (miscInfo.isClearance || miscInfo.clearanceItem)
    ? isBossClearanceProductEnabled && bossEligibility
    : bossEligibility;
}

/**
 * @method validateBopisEligibility
 * @description checks the bopis product flags for returning the boolean
 * value
 * @param {bool} isBopisClearanceProductEnabled this flag is derived through the kill
 * Switch API. If a product is of Clearance then we need to check this kill switch
 * @param {bool} isBopisEnabled this flag is a global bopis Enability flag, which is
 * derived from the UserRegisteredInfo API. Backend sends this flag along with
 * validating country and state bopis availability
 * @param {object} miscInfo object data with pickup eligibility of product
 */
function validateBopisEligibility({ isBopisClearanceProductEnabled, isBopisEnabled, miscInfo }) {
  const bopisEligibility = isBopisEnabled && miscInfo && miscInfo.isBopisEligible;
  // productDynamicAbstractor and cartDynamicAbstractor return different keys for clearance item
  return miscInfo && (miscInfo.isClearance || miscInfo.clearanceItem)
    ? isBopisClearanceProductEnabled && bopisEligibility
    : bopisEligibility;
}

/**
 * @method isProductOOS
 * @description checks if the selected size variant is having available
 * quantity
 */
function isProductOOS(colorFitsSizesMap, selectedSKu) {
  const currentFitEntry = getMapSliceForFit(colorFitsSizesMap, selectedSKu.color, selectedSKu.Fit);
  if (currentFitEntry && currentFitEntry.sizes) {
    const selectedSKuProductInfo = currentFitEntry.sizes.find(
      (size) => size.sizeName === selectedSKu.Size
    );
    const maxAvailableProducts = selectedSKuProductInfo ? selectedSKuProductInfo.maxAvailable : 0;

    return maxAvailableProducts < 1;
  }
  return true;
}

/**
 * @method isBOSSProductOOS
 * @description checks if the selected size variant is having available
 * quantity
 */
function isBOSSProductOOS(colorFitsSizesMap, selectedSKu) {
  const currentFitEntry = getMapSliceForFit(colorFitsSizesMap, selectedSKu.color, selectedSKu.Fit);
  if (currentFitEntry && currentFitEntry.sizes) {
    const selectedSKuProductInfo = currentFitEntry.sizes.find(
      (size) => size.sizeName === selectedSKu.Size
    );
    const maxAvailableBossProducts = selectedSKuProductInfo
      ? selectedSKuProductInfo.maxAvailableBoss
      : 0;

    return maxAvailableBossProducts < 1;
  }
  return true;
}

/*
 * @method numericStringToBool
 * @description this method returns the bool value of string numeric passed
 * @param {string} str the  string numeric value
 */
const numericStringToBool = (str) => !!+str;

/**
 *
 * @param {object} event the HTML element's element
 * @param {number} key key for which the event needs to be triggered
 * @param {function} method method passed which is to be invoked.
 * @description this method invokes the parameter method received when respective
 * keybord key is triggered
 */

function handleGenericKeyDown(event, key, method) {
  if (event.keyCode === key) {
    method();
  }
  return null;
}

function getStoreInformation(userDefaultStore, userNearByStore) {
  if (userDefaultStore && userDefaultStore?.basicInfo) {
    const { basicInfo } = userDefaultStore;
    return {
      storeName: basicInfo?.storeName,
      city: basicInfo?.address?.city,
      state: basicInfo?.address?.state,
    };
  }
  if (userNearByStore && userNearByStore[0]) {
    const { properties } = userNearByStore[0];
    return {
      storeName: properties?.storeName,
      city: properties?.city,
      state: properties?.state,
    };
  }
  return null;
}

const getStatusFromBOPIS = (
  bopisItemInventory,
  defaultSuggestedStore,
  isBopisMultipleStoreEnabled
) => {
  if (isBopisMultipleStoreEnabled) {
    const defaultStoreId = defaultSuggestedStore && defaultSuggestedStore[0]?.basicInfo?.id;
    const filterInventoryResponse = bopisItemInventory?.inventoryResponse?.filter(
      (store) => store?.storeId === defaultStoreId
    );

    return (
      filterInventoryResponse && filterInventoryResponse[0] && filterInventoryResponse[0].status
    );
  }
  const bopisItemInventoryRes =
    bopisItemInventory &&
    bopisItemInventory.inventoryResponse &&
    bopisItemInventory.inventoryResponse[0];
  return bopisItemInventoryRes && bopisItemInventoryRes.status;
};

const getOOBErrorBOPIS = (bopisItemInventory) => {
  const bopisItemInventoryError =
    bopisItemInventory && bopisItemInventory.error && bopisItemInventory.error.errorCode;
  return bopisItemInventoryError && bopisItemInventoryError === 'OOB_ERROR';
};

const capitalizeEveryFirstLetterOfWord = (params) => {
  if (params && typeof params === 'string') {
    return params
      .split(' ')
      .map((element) => {
        return element[0].toUpperCase() + element.slice(1, element.length);
      })
      .join(' ');
  }
  return null;
};

const isVariantHasPreOrder = (variantsPreOrderMap) => {
  if (!Array.isArray(variantsPreOrderMap)) return false;
  let count = 0;
  variantsPreOrderMap?.forEach((variant) => {
    if (variant?.preOrderDate) {
      count += 1;
    }
  });
  return variantsPreOrderMap?.length === count;
};

const filterAvailableStores = (
  availabilityList,
  bopisStoreList,
  storecount,
  requestedQuantity = 0
) => {
  const storeIdList = {};
  const count = storecount || 100;
  const tempList = availabilityList?.filter((store) => store?.quantity >= requestedQuantity);
  tempList?.forEach((store) => {
    storeIdList[store.storeId] = store.status;
  });
  const nearestAvailableStoreList = bopisStoreList?.filter((store) =>
    Object.keys(storeIdList)?.includes(store?.basicInfo?.id)
  );
  nearestAvailableStoreList?.forEach((store) => {
    const tmpObj = store;
    tmpObj.atcStatus = storeIdList[store?.basicInfo?.id];
    tmpObj.productAvailability.status = BOPIS_ITEM_AVAILABILITY.AVAILABLE;
  });
  return nearestAvailableStoreList.slice(0, count);
};

const filterNearByStore = (stores, storecount) => {
  const count = storecount || 100;
  return stores?.filter((store) => store?.productAvailability?.status === 'OK').slice(0, count);
};

const filterBossEligibleStores = (stores) => {
  return stores?.filter((store) => store?.storeBossInfo?.isBossEligible === true);
};

const filterDefaultStore = (stores, userDefaultFavStore) => {
  return stores?.filter(
    (store) => store?.basicInfo?.storeName === userDefaultFavStore?.basicInfo?.storeName
  );
};

const filterStoreIdList = (stores) => {
  const storeIdList = [];
  stores?.forEach((store) => {
    storeIdList.push(store?.basicInfo?.id);
  });
  return storeIdList;
};

export {
  numericStringToBool,
  validateSkuDetails,
  validateBossEligibility,
  validateBopisEligibility,
  isProductOOS,
  isBOSSProductOOS,
  handleGenericKeyDown,
  getStoreInformation,
  getStatusFromBOPIS,
  getOOBErrorBOPIS,
  capitalizeEveryFirstLetterOfWord,
  isVariantHasPreOrder,
  filterAvailableStores,
  filterNearByStore,
  filterBossEligibleStores,
  filterDefaultStore,
  filterStoreIdList,
};
